import React, { useState } from 'react';
import { Compass, ExternalLink, Menu, Search, ShoppingCart, User, Layers } from 'lucide-react';
import { resolveMediaUrl, handleImageError } from '../../../utils/mediaResolver';

export default function NavigationPreview({ form = {}, navigationOptions = [] }) {
  const [navMode, setNavMode] = useState('desktop'); // 'desktop' | 'mobile'

  const menuType = form.menu_type || (form.parent_id ? 'submenu' : 'main');
  const destType = form.destination_type || (form.linked_page_id || String(form.url || '').startsWith('/content/') ? 'dynamic' : form.url ? 'custom' : 'dropdown');
  const parentMenu = navigationOptions.find((item) => String(item.id) === String(form.parent_id));

  const pageItems = Array.isArray(form.page_items) ? form.page_items : [];

  return (
    <div className="bg-slate-50 min-h-full flex flex-col font-sans">
      {/* Top Controls */}
      <div className="bg-white border-b px-4 py-2.5 flex flex-wrap items-center justify-between gap-2 text-xs">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-500">Menu Type:</span>
          <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 font-bold uppercase text-[10px]">
            {menuType === 'main' ? 'Main Navbar Menu' : `Submenu under ${parentMenu?.label || 'Main Menu'}`}
          </span>
          <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-semibold">
            Destination: {destType}
          </span>
        </div>

        <div className="flex bg-slate-100 p-0.5 rounded-lg border text-[11px]">
          <button
            type="button"
            onClick={() => setNavMode('desktop')}
            className={`px-2.5 py-0.5 rounded font-medium transition ${
              navMode === 'desktop' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
            }`}
          >
            Desktop Header
          </button>
          <button
            type="button"
            onClick={() => setNavMode('mobile')}
            className={`px-2.5 py-0.5 rounded font-medium transition ${
              navMode === 'mobile' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
            }`}
          >
            Mobile Menu
          </button>
        </div>
      </div>

      {/* Navbar Simulation */}
      <div className="p-6 md:p-8 space-y-6 flex-1 flex flex-col">
        {navMode === 'desktop' ? (
          /* Desktop Header Mockup */
          <div className="bg-white rounded-2xl border shadow-md overflow-hidden">
            {/* Main Header Bar */}
            <div className="px-6 py-3.5 border-b flex items-center justify-between gap-4 bg-slate-900 text-white">
              <div className="flex items-center gap-6">
                <span className="text-xl font-black tracking-wider text-white">ZOBOX</span>
                <div className="hidden sm:flex items-center gap-2 bg-slate-800 px-3 py-1.5 rounded-lg text-xs text-slate-400 w-56">
                  <Search className="w-3.5 h-3.5" />
                  <span>Search refurbished devices...</span>
                </div>
              </div>

              {/* Navigation links with Current Item Highlighted */}
              <div className="flex items-center gap-4 text-xs font-semibold">
                <span className="text-slate-300 hover:text-white">Buy</span>
                <span className="text-slate-300 hover:text-white">Sell</span>

                {/* The Current Active Nav Item */}
                <div className="relative group">
                  <span className="px-2.5 py-1 rounded bg-[#2B3380] text-white border border-indigo-400 flex items-center gap-1.5 shadow-sm">
                    {form.icon && <span>{form.icon}</span>}
                    <span>{form.label || 'New Item'}</span>
                    {form.badge && (
                      <span className="bg-amber-400 text-slate-900 text-[9px] font-black px-1.5 py-0.2 rounded">
                        {form.badge}
                      </span>
                    )}
                    {destType === 'dropdown' && <span className="text-[10px]">▼</span>}
                  </span>

                  {destType === 'dropdown' && (
                    <div className="absolute top-full left-0 mt-1 w-44 bg-white text-slate-800 rounded-lg shadow-xl border p-2 text-xs space-y-1 z-10">
                      <div className="text-gray-400 text-[10px] uppercase font-bold px-2 py-1">Submenu Preview</div>
                      <div className="px-2 py-1 rounded hover:bg-slate-100 font-medium">Sample Submenu 1</div>
                      <div className="px-2 py-1 rounded hover:bg-slate-100 font-medium">Sample Submenu 2</div>
                    </div>
                  )}
                </div>

                <span className="text-slate-300 hover:text-white">Stores</span>
                <span className="text-slate-300 hover:text-white">More ▼</span>
              </div>

              <div className="flex items-center gap-3 text-slate-300 text-xs">
                <ShoppingCart className="w-4 h-4" />
                <User className="w-4 h-4" />
              </div>
            </div>

            {/* Destination Preview Explanation */}
            <div className="p-4 bg-slate-50 text-xs text-slate-600 flex justify-between items-center border-t">
              <span>
                Click destination:{' '}
                <strong className="font-mono text-slate-800">
                  {destType === 'dynamic' ? `/content/${form.page_slug || 'dynamic-page'}` : (form.url || 'Dropdown only')}
                </strong>
              </span>
              {form.new_tab && <span className="text-indigo-700 font-semibold flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Opens in new tab</span>}
            </div>
          </div>
        ) : (
          /* Mobile Drawer Mockup */
          <div className="max-w-xs mx-auto w-full bg-white rounded-2xl border shadow-md overflow-hidden text-xs">
            <div className="bg-slate-900 text-white p-4 flex items-center justify-between">
              <span className="font-black text-base">ZOBOX</span>
              <Menu className="w-4 h-4" />
            </div>
            <div className="p-4 divide-y space-y-2">
              <div className="py-2 text-slate-700 font-medium">Buy Mobiles &amp; Laptops</div>
              <div className="py-2 text-slate-700 font-medium">Sell Device</div>
              <div className="py-2 bg-indigo-50 text-[#2B3380] font-bold px-2 rounded-lg flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  {form.icon && <span>{form.icon}</span>}
                  <span>{form.label || 'Active Menu Item'}</span>
                </span>
                {form.badge && (
                  <span className="bg-[#2B3380] text-white text-[9px] px-1.5 py-0.2 rounded font-black">
                    {form.badge}
                  </span>
                )}
              </div>
              <div className="py-2 text-slate-700 font-medium">Corporate Desk</div>
              <div className="py-2 text-slate-700 font-medium">Stores &amp; Support</div>
            </div>
          </div>
        )}

        {/* If Destination is Dynamic Page, show complete preview of that dynamic page */}
        {destType === 'dynamic' && (
          <div className="border rounded-2xl bg-white p-6 shadow-sm space-y-6">
            <div className="flex items-center gap-2 border-b pb-3 text-xs font-bold text-slate-500 uppercase tracking-wider">
              <Compass className="w-4 h-4 text-[#2B3380]" />
              <span>Dynamic Landing Page Preview (/content/{form.page_slug || 'slug'})</span>
            </div>

            {form.page_featured_image && (
              <img
                src={resolveMediaUrl(form.page_featured_image)}
                alt=""
                className="w-full h-40 object-cover rounded-xl border"
                onError={handleImageError}
              />
            )}

            <div>
              <h2 className="text-xl md:text-2xl font-extrabold text-gray-900">
                {form.page_title || form.label || 'Dynamic Page Title'}
              </h2>
              {form.page_excerpt && (
                <p className="text-xs text-gray-600 mt-2 leading-relaxed italic border-l-2 border-[#2B3380] pl-3">
                  {form.page_excerpt}
                </p>
              )}
            </div>

            {form.page_content && (
              <div className="text-xs text-gray-700 whitespace-pre-line leading-relaxed">
                {form.page_content}
              </div>
            )}

            {pageItems.length > 0 && (
              <div className="space-y-3 pt-2">
                <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block">
                  Dynamic Page Items ({pageItems.length})
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {pageItems.map((item, idx) => (
                    <div key={idx} className="border rounded-lg p-3 bg-slate-50 text-xs">
                      {item.eyebrow && <span className="text-[10px] font-bold text-[#2B3380] uppercase">{item.eyebrow}</span>}
                      <strong className="block text-gray-900 mt-0.5">{item.title}</strong>
                      <p className="text-gray-500 mt-1">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
